import React, { useState, useEffect } from 'react';
import util from '../../utils/util';

import {
    Card, Button, Table, Modal, Tag, message,
    Row, Col, Alert, Spin, Select, Progress, Divider
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined,
    EditOutlined, EyeOutlined, StarFilled,
    ReloadOutlined, FilterOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import { usePeriods } from '../../hooks/usePeriods';
import moment from 'moment';
import {
    trailAssessmentApprove, trailAssessmentReject, trailAssessmentRevision,
    TRAIL_MODULES
} from '../../utils/activityTrail';
const { Option } = Select;
const { TextArea } = require('antd').Input;

const RATING = [
    { min:98,  max:100,  label:'Outstanding',          stars:5, color:'#389e0d' },
    { min:95,  max:97.9, label:'Excellent',            stars:4, color:'#1890ff' },
    { min:90,  max:94.9, label:'Good',                 stars:3, color:'#52c41a' },
    { min:85,  max:89.9, label:'Fair',                 stars:2, color:'#faad14' },
    { min:70,  max:84.9, label:'Requires Improvement', stars:1, color:'#fa8c16' },
    { min:0,   max:69.9, label:'Inadequate',           stars:0, color:'#f5222d' },
];
const getRating = s => RATING.find(r => s >= r.min && s <= r.max) || RATING[RATING.length-1];

// Quarters are computed RELATIVE to the selected period's own period_start —
// not calendar-year quarters. So if a period starts 01 Apr, Q1 = Apr-Jun,
// Q2 = Jul-Sep, Q3 = Oct-Dec, Q4 = Jan-Mar, matching whatever fiscal
// convention that period actually uses.
const getQuarterRanges = (period) => {
    if (!period?.period_start) return {};
    const start = moment(period.period_start).startOf('day');
    const ranges = {};
    ['Q1','Q2','Q3','Q4'].forEach((q, i) => {
        const qStart = start.clone().add(i*3, 'months');
        const qEnd   = start.clone().add((i+1)*3, 'months').subtract(1, 'day');
        ranges[q] = [qStart, qEnd];
    });
    return ranges;
};

const CLAUSE_COLORS = {
    AACC:'#0ea5e9', COP:'#8b5cf6', MOM:'#f59e0b', PRPE:'#10b981',
    IPCM:'#ef4444', PSQI:'#3b82f6', GROM:'#6366f1', FMS:'#ec4899',
    HRE:'#14b8a6',  ISM:'#a855f7',
};

function Stars({ count, size=12 }) {
    return <span>{[1,2,3,4,5].map(i=>(
        <StarFilled key={i} style={{ color: i<=count?'#faad14':'#e8e8e8', fontSize:size }}/>
    ))}</span>;
}

export default function QoCApproval() {
    const { periods, currentPeriod } = usePeriods();
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading]         = useState(false);
    // Permission checks
    const canAdd    = util.canAdd('qoc_approve');
    const canEdit   = util.canEdit('qoc_approve');
    const canDelete = util.canDelete('qoc_approve');
    const canExport = util.canExport('qoc_approve');
    const [filters, setFilters]         = useState({ status:'submitted', facility_id:'', period_label:'', region:'', clause_id:'', quarters:[] });
    const [facilities, setFacilities]   = useState([]);
    const [standards, setStandards]     = useState([]);
    const [clauses, setClauses]         = useState([]);
    const [reviewing, setReviewing]     = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [remarks, setRemarks]         = useState('');
    const [meResponses, setMeResponses] = useState([]);

    useEffect(() => {
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data || []));
        QoCService.getStandards().then(r => setStandards(r.data?.data || []));
        QoCService.getClauses().then(r => setClauses(r.data?.data || []));
    }, []);

    useEffect(() => {
        if (currentPeriod && !filters.period_label) {
            setFilters(f => ({ ...f, period_label: currentPeriod.label }));
        }
    }, [currentPeriod?.label]);

    // eslint-disable-next-line
    useEffect(() => { load(); }, [filters]);

    const load = async () => {
        setLoading(true);
        try {
            // Find period dates from selected label
            const selectedPeriod = periods.find(p => p.label === filters.period_label);
            const res = await QoCService.getAssessments({
                status: filters.status || 'submitted',
                ...(filters.facility_id  && { facility_id: filters.facility_id }),
                ...(filters.region       && { region: filters.region }),
                ...(filters.clause_id    && { clause_id: filters.clause_id }),
                ...(selectedPeriod && { from_date: selectedPeriod.period_start, to_date: selectedPeriod.period_end }),
            });
            let data = res.data?.data || [];
            // Quarter drill-down — only meaningful once a period is
            // selected, since quarters are computed relative to THAT
            // period's own start date. Multiple quarters selected = union
            // of their ranges. Client-side since it's not a single date
            // range the backend can filter on directly.
            if (filters.quarters?.length && selectedPeriod) {
                const ranges = getQuarterRanges(selectedPeriod);
                data = data.filter(a => {
                    const d = moment(a.assessment_date);
                    return filters.quarters.some(q => {
                        const r = ranges[q];
                        return r && d.isSameOrAfter(r[0]) && d.isSameOrBefore(r[1]);
                    });
                });
            }
            setAssessments(data);
        } catch { message.error('Failed to load'); }
        finally { setLoading(false); }
    };

    const openReview = async (assessment) => {
        setReviewing(assessment);
        setRemarks('');
        setReviewLoading(true);
        try {
            const res = await QoCService.getAssessmentResponsesME(assessment.id);
            setMeResponses(res.data?.data || []);
        } catch { message.error('Failed to load responses'); }
        finally { setReviewLoading(false); }
    };

    const handleApprove = async () => {
        setReviewLoading(true);
        try {
            await QoCService.approveAssessment(reviewing.id, { remarks });
            trailAssessmentApprove(reviewing.facility_name, reviewing.id);
            message.success('Assessment approved — scores will now appear in dashboards');
            setReviewing(null);
            load();
        } catch { message.error('Approval failed'); }
        finally { setReviewLoading(false); }
    };

    const handleReject = async () => {
        if (!remarks.trim()) { message.error('Please provide a reason for rejection'); return; }
        setReviewLoading(true);
        try {
            await QoCService.rejectAssessment(reviewing.id, { remarks });
            trailAssessmentReject(reviewing.facility_name, reviewing.id, remarks);
            message.success('Assessment rejected');
            setReviewing(null);
            load();
        } catch { message.error('Rejection failed'); }
        finally { setReviewLoading(false); }
    };

    const handleRequestRevision = async () => {
        if (!remarks.trim()) { message.error('Please describe what needs to be revised'); return; }
        setReviewLoading(true);
        try {
            await QoCService.requestRevision(reviewing.id, { remarks });
            trailAssessmentRevision(reviewing.facility_name, reviewing.id, remarks);
            message.success('Revision requested — assessment returned to assessor');
            setReviewing(null);
            load();
        } catch { message.error('Failed'); }
        finally { setReviewLoading(false); }
    };

    const columns = [
        {
            title: 'Facility', dataIndex: 'facility_name', width: 200, ellipsis: true,
            render: (name, a) => (
                <div>
                    <div style={{ fontWeight:700, fontSize:12 }}>{name}</div>
                    <div style={{ fontSize:10, color:'#888' }}>{a.site_type}</div>
                </div>
            )
        },
        {
            title: 'Clause', width: 90,
            render: (_, a) => {
                const code = a.clause_code;
                if (!code) return <Tag>Full</Tag>;
                const col = CLAUSE_COLORS[code] || '#1890ff';
                return (
                    <div style={{ width:28, height:28, borderRadius:5, background:col,
                        color:'#fff', display:'flex', alignItems:'center',
                        justifyContent:'center', fontSize:9, fontWeight:800 }}>
                        {code}
                    </div>
                );
            }
        },
        {
            title: 'Audit Date', dataIndex: 'assessment_date', width: 110,
            render: d => moment(d).format('DD MMM YYYY')
        },
        {
            title: 'Assessor', dataIndex: 'assessor_name', width: 130 },
        {
            title: 'Score', width: 100,
            render: (_, a) => {
                const pct = parseFloat(a.overall_percentage || 0);
                const r = getRating(pct);
                return (
                    <div>
                        <span style={{ fontWeight:900, fontSize:14, color:r.color }}>{pct.toFixed(1)}%</span>
                        <div><Stars count={r.stars} size={10}/></div>
                    </div>
                );
            }
        },
        {
            title: 'Status', dataIndex: 'status', width: 120,
            render: s => {
                const cfg = { submitted:{color:'blue',label:'Pending Review'},
                    approved:{color:'green',label:'Approved'},
                    rejected:{color:'red',label:'Rejected'},
                    revision:{color:'orange',label:'Revision Requested'} };
                const c = cfg[s] || { color:'default', label:s };
                return <Tag color={c.color} style={{ fontWeight:700, fontSize:10 }}>{c.label}</Tag>;
            }
        },
        {
            title: 'Submitted', dataIndex: 'updated_at', width: 110,
            render: d => <span style={{ fontSize:11, color:'#888' }}>{moment(d).fromNow()}</span>
        },
        {
            title: '', width: 90,
            render: (_, a) => (
                <Button size="small" type="primary" icon={<EyeOutlined/>}
                    onClick={() => openReview(a)} style={{ fontSize:11 }}>
                    Review
                </Button>
            )
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Assessment Approval Queue</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        Review and approve submitted assessments before they appear in dashboards
                    </div>
                </div>
                <div className="my-auto ml-auto">
                    <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>Refresh</Button>
                </div>
            </div>

            <div className="page-pad">
                <Row gutter={12} style={{ marginBottom:16 }}>
                    {[
                        { label:'Pending Review',     val: assessments.filter(a=>a.status==='submitted').length, color:'#1890ff', key:'submitted'  },
                        { label:'Approved',           val: assessments.filter(a=>a.status==='approved').length,  color:'#52c41a', key:'approved'   },
                        { label:'Rejected',           val: assessments.filter(a=>a.status==='rejected').length,  color:'#f5222d', key:'rejected'   },
                        { label:'Revision Requested', val: assessments.filter(a=>a.status==='revision').length,  color:'#fa8c16', key:'revision'   },
                    ].map(s => (
                        <Col key={s.key} span={6}>
                            <div onClick={() => setFilters(f => ({ ...f, status: s.key }))}
                                style={{ background: filters.status===s.key ? `${s.color}15` : '#fafbff',
                                    border: `2px solid ${filters.status===s.key ? s.color : '#e8eeff'}`,
                                    borderRadius:10, padding:'14px 18px', cursor:'pointer', transition:'all 0.15s' }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8 }}>{s.label}</div>
                                <div style={{ fontSize:26, fontWeight:900, color:s.color }}>{s.val}</div>
                            </div>
                        </Col>
                    ))}
                </Row>

                <Card size="small" style={{ marginBottom:14 }}>
                    <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                        <FilterOutlined style={{ color:'#94a3b8' }}/>
                        <Select style={{ width:180 }} value={filters.status}
                            onChange={v => setFilters(f => ({ ...f, status:v }))}>
                            <Option value="submitted">Pending Review</Option>
                            <Option value="approved">Approved</Option>
                            <Option value="rejected">Rejected</Option>
                            <Option value="revision">Revision Requested</Option>
                        </Select>
                        <Select allowClear placeholder="All Facilities" style={{ width:220 }}
                            showSearch optionFilterProp="children"
                            value={filters.facility_id || undefined}
                            onChange={v => setFilters(f => ({ ...f, facility_id:v||'' }))}>
                            {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                        </Select>
                        <Select allowClear placeholder="All Regions" style={{ width:160 }}
                            value={filters.region || undefined}
                            onChange={v => setFilters(f => ({ ...f, region:v||'' }))}>
                            {[...new Set(facilities.map(f=>f.located_region||f.region).filter(Boolean))].sort().map(r => (
                                <Option key={r} value={r}>{r}</Option>
                            ))}
                        </Select>
                        <Select allowClear placeholder="All Clauses" style={{ width:220 }}
                            showSearch optionFilterProp="children"
                            value={filters.clause_id || undefined}
                            onChange={v => setFilters(f => ({ ...f, clause_id:v||'' }))}>
                            {standards.map(std => (
                                <Select.OptGroup key={std.id} label={std.name}>
                                    {clauses.filter(c => String(c.standard_id) === String(std.id)).map(c => (
                                        <Option key={c.id} value={c.id}>{c.code} — {c.title}</Option>
                                    ))}
                                </Select.OptGroup>
                            ))}
                        </Select>
                        <Select allowClear placeholder="All Periods" style={{ width:160 }}
                            value={filters.period_label || undefined}
                            onChange={v => setFilters(f => ({ ...f, period_label:v||'', quarters:[] }))}>
                            {periods.map(p => (
                                <Option key={p.label} value={p.label}>
                                    {parseInt(p.is_current) === 1 ? '★ ' : ''}{p.label}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            mode="multiple" allowClear
                            placeholder={filters.period_label ? 'All quarters' : 'Select a period first'}
                            disabled={!filters.period_label}
                            style={{ width:220 }}
                            value={filters.quarters || []}
                            onChange={v => setFilters(f => ({ ...f, quarters:v }))}>
                            {['Q1','Q2','Q3','Q4'].map(q => {
                                const selPeriod = periods.find(p => p.label === filters.period_label);
                                const range = selPeriod ? getQuarterRanges(selPeriod)[q] : null;
                                return (
                                    <Option key={q} value={q}>
                                        {q}
                                        {range && (
                                            <span style={{ color:'#aaa', marginLeft:6, fontSize:11 }}>
                                                ({range[0].format('DD MMM')} – {range[1].format('DD MMM YYYY')})
                                            </span>
                                        )}
                                    </Option>
                                );
                            })}
                        </Select>
                        {(filters.facility_id || filters.region || filters.clause_id || filters.quarters?.length > 0) && (
                            <Button size="small" onClick={() => setFilters(f => ({
                                ...f, facility_id:'', region:'', clause_id:'', quarters:[]
                            }))}>Clear</Button>
                        )}
                    </div>
                </Card>

                {assessments.filter(a=>a.status==='submitted').length > 0 && filters.status === 'submitted' && (
                    <Alert
                        message={`${assessments.filter(a=>a.status==='submitted').length} assessments waiting for your approval`}
                        description="Approved assessments will appear in the Executive Dashboard, Analytics, and Quality Index."
                        type="warning" showIcon style={{ marginBottom:14 }}/>
                )}

                <Card size="small">
                    <Table columns={columns} dataSource={assessments} rowKey="id"
                        loading={loading} pagination={{ pageSize:20, showTotal:t=>`${t} assessments` }}/>
                </Card>
            </div>

            {reviewing && (
                <Modal open onCancel={() => setReviewing(null)} footer={null}
                    width={980} style={{ top:10 }} destroyOnClose
                    title={
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{ width:40, height:40, borderRadius:8,
                                background: CLAUSE_COLORS[reviewing.clause_code]||'#1890ff',
                                color:'#fff', display:'flex', alignItems:'center',
                                justifyContent:'center', fontWeight:900, fontSize:11,
                                textAlign:'center', lineHeight:1.2 }}>
                                {reviewing.clause_code || '?'}
                            </div>
                            <div>
                                <div style={{ fontWeight:800 }}>
                                    {reviewing.facility_name} — {reviewing.clause_code || 'Full Audit'}
                                </div>
                                <div style={{ fontSize:11, color:'#888' }}>
                                    Audited: {moment(reviewing.assessment_date).format('DD MMM YYYY')} •
                                    Assessor: {reviewing.assessor_name} •
                                    Score: <strong style={{ color: getRating(parseFloat(reviewing.overall_percentage||0)).color }}>
                                        {parseFloat(reviewing.overall_percentage||0).toFixed(1)}%
                                    </strong>
                                </div>
                            </div>
                            <Tag color="blue" style={{ marginLeft:'auto', fontWeight:700 }}>
                                {reviewing.status === 'submitted' ? 'Pending Review' : reviewing.status}
                            </Tag>
                        </div>
                    }>
                    {reviewLoading ? (
                        <div style={{ textAlign:'center', padding:40 }}><Spin size="large"/></div>
                    ) : (
                        <div>
                            {(() => {
                                const pct = parseFloat(reviewing.overall_percentage || 0);
                                const r = getRating(pct);
                                return (
                                    <div style={{ display:'flex', gap:16, marginBottom:16,
                                        background:`${r.color}10`, border:`1px solid ${r.color}40`,
                                        borderRadius:8, padding:'12px 16px', alignItems:'center' }}>
                                        <div>
                                            <div style={{ fontSize:36, fontWeight:900, color:r.color }}>{pct.toFixed(1)}%</div>
                                            <Stars count={r.stars} size={16}/>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight:800, fontSize:15, color:r.color }}>{r.label}</div>
                                            <div style={{ fontSize:12, color:'#666' }}>
                                                ✓ {meResponses.filter(m=>m.fulfillment==='Met').length} Met •
                                                ◑ {meResponses.filter(m=>m.fulfillment==='Partially Met').length} Partial •
                                                ✗ {meResponses.filter(m=>m.fulfillment==='Not Met').length} Not Met •
                                                — {meResponses.filter(m=>m.fulfillment==='Not Applicable').length} NA
                                            </div>
                                        </div>
                                        <div style={{ flex:1 }}>
                                            <Progress percent={Math.min(100,pct)} strokeColor={r.color} showInfo={false}/>
                                        </div>
                                    </div>
                                );
                            })()}

                            <div style={{ maxHeight:'42vh', overflowY:'auto', marginBottom:14 }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:10 }}>
                                    <thead>
                                        <tr style={{ background:'#f0f5ff', position:'sticky', top:0 }}>
                                            {['Ref No','Measurable Element','Score','Comment','Observation','Criticality','Good Practice'].map(h => (
                                                <th key={h} style={{ padding:'6px 8px', textAlign:'left',
                                                    borderBottom:'2px solid #dbeafe', fontWeight:700,
                                                    fontSize:10, color:'#1a1a2e', whiteSpace:'nowrap' }}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {meResponses.map((me, i) => {
                                            const fcolor = me.fulfillment==='Met' ? '#389e0d'
                                                : me.fulfillment==='Partially Met' ? '#fa8c16'
                                                : me.fulfillment==='Not Met' ? '#f5222d'
                                                : '#888';
                                            const bg = me.fulfillment==='Not Met' ? '#fff2f0'
                                                : me.fulfillment==='Partially Met' ? '#fffbe6'
                                                : i%2 ? '#fafafa' : '#fff';
                                            const critColor = me.criticality_of_observation==='high' ? '#f5222d'
                                                : me.criticality_of_observation==='medium' ? '#fa8c16' : '#52c41a';
                                            return (
                                                <tr key={me.id||i} style={{ background:bg }}>
                                                    <td style={{ padding:'5px 8px', fontFamily:'monospace', fontWeight:700,
                                                        color: me.is_critical===1 ? '#f5222d':'#555', whiteSpace:'nowrap', fontSize:10 }}>
                                                        {me.is_critical===1 && '⚠ '}{me.ref_no}
                                                    </td>
                                                    <td style={{ padding:'5px 8px', maxWidth:200, fontSize:10, lineHeight:1.4 }}>
                                                        {(me.me_text||'').slice(0,80)}{me.me_text?.length>80?'…':''}
                                                    </td>
                                                    <td style={{ padding:'5px 8px', whiteSpace:'nowrap' }}>
                                                        {me.fulfillment ? (
                                                            <Tag style={{ background:fcolor, color:'#fff', border:'none', fontSize:9, fontWeight:700 }}>
                                                                {me.fulfillment==='Met'?'✓ M'
                                                                :me.fulfillment==='Partially Met'?'◑ PM'
                                                                :me.fulfillment==='Not Met'?'✗ NM'
                                                                :me.fulfillment==='Not Applicable'?'— NA':'? NC'}
                                                            </Tag>
                                                        ) : <span style={{ color:'#ddd' }}>—</span>}
                                                    </td>
                                                    <td style={{ padding:'5px 8px', fontSize:9, color:'#555', maxWidth:140 }}>{me.comment||''}</td>
                                                    <td style={{ padding:'5px 8px', fontSize:9, color:'#555', maxWidth:120 }}>{me.general_observation||''}</td>
                                                    <td style={{ padding:'5px 8px', textAlign:'center' }}>
                                                        {me.criticality_of_observation && me.criticality_of_observation !== 'na' && (
                                                            <Tag style={{ background:'transparent', color:critColor,
                                                                border:`1px solid ${critColor}`, fontSize:9, padding:'0 4px' }}>
                                                                {me.criticality_of_observation}
                                                            </Tag>
                                                        )}
                                                    </td>
                                                    <td style={{ padding:'5px 8px', fontSize:9, color:'#52c41a' }}>{me.area_of_good_practice||''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {reviewing.status === 'submitted' && (
                                <>
                                    <Divider style={{ margin:'10px 0' }}/>
                                    <div style={{ marginBottom:8 }}>
                                        <label style={{ fontWeight:700, fontSize:12, display:'block', marginBottom:4 }}>
                                            Reviewer Remarks
                                            <span style={{ fontWeight:400, color:'#888', marginLeft:6, fontSize:11 }}>
                                                (required for Reject / Revision, optional for Approve)
                                            </span>
                                        </label>
                                        <TextArea rows={3} value={remarks}
                                            onChange={e => setRemarks(e.target.value)}
                                            placeholder="Enter comments about this assessment..."/>
                                    </div>
                                    <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                                        <Button icon={<EditOutlined/>} onClick={handleRequestRevision}
                                            loading={reviewLoading}
                                            style={{ borderColor:'#fa8c16', color:'#fa8c16' }}>
                                            Request Revision
                                        </Button>
                                        <Button danger icon={<CloseCircleOutlined/>}
                                            onClick={handleReject} loading={reviewLoading}>
                                            Reject
                                        </Button>
                                        <Button type="primary" icon={<CheckCircleOutlined/>}
                                            onClick={handleApprove} loading={reviewLoading}
                                            style={{ background:'#52c41a', borderColor:'#52c41a' }}>
                                            Approve
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </Modal>
            )}
        </div>
    );
}